/**
 * A sessao do usuario, do ponto de vista das telas.
 *
 * **Todo** acesso a sessao passa por aqui: a guarda de rota, o shell, a tela de login e o
 * botao "Sair". Uma tela que chamasse `sessaoAtual()` direto teria uma copia propria do
 * estado, e o shell continuaria mostrando o usuario antigo depois do logout.
 *
 * A chave e `['sessao']` e o cache e a **unica** memoria de quem entrou: nada de token, de
 * senha ou de sessao em `localStorage`/`sessionStorage` (AD-5). As credenciais viajam em
 * cookie `HttpOnly`, que o JavaScript nao le — recarregar a pagina refaz `GET /sessoes/atual`
 * e o cookie responde por quem e.
 *
 * `retry: false` de proposito: `401` aqui significa "nao ha sessao", e nao "a rede falhou".
 * Repetir a chamada so atrasaria o redirecionamento para a tela de entrada.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { type Credenciais, type Sessao, entrar, sair, sessaoAtual } from './api'

/** A chave do cache. Escrita uma vez; quem precisa dela a importa. */
export const CHAVE_DA_SESSAO = ['sessao'] as const

/**
 * A consulta da sessao.
 *
 * Exportada como `queryOptions` — e nao como hook — porque a guarda de rota a usa fora de
 * componente, com `ensureQueryData`: assim a guarda e o shell compartilham **uma** entrada de
 * cache, e a tela nao repete a chamada que a guarda acabou de fazer.
 *
 * `revalidateIfStale` **nao** entra aqui, e sim na chamada da guarda
 * (`src/rotas/aplicacao.tsx`): ele e opcao de `ensureQueryData`, e nao da consulta. A decisao
 * esta documentada la — em resumo, sem ele o cache nunca revalidaria e um usuario desativado
 * seguiria com o shell montado ate a primeira mutacao falhar.
 */
export const consultaDaSessao = queryOptions({
  queryKey: CHAVE_DA_SESSAO,
  queryFn: sessaoAtual,
  retry: false,
})

/** Quem esta autenticado agora, para quem ja esta dentro do shell. */
export function useSessao() {
  return useQuery(consultaDaSessao)
}

/**
 * Entrar.
 *
 * O sucesso grava a sessao no cache com a **resposta do proprio login**: `POST /sessoes`
 * devolve o mesmo corpo de `GET /sessoes/atual`, entao um `GET` logo depois seria uma ida a
 * rede para saber o que a resposta anterior ja disse.
 */
export function useEntrar() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (credenciais: Credenciais) => entrar(credenciais),
    onSuccess: (sessao: Sessao) => {
      clienteDeConsultas.setQueryData(CHAVE_DA_SESSAO, sessao)
    },
  })
}

/**
 * Sair.
 *
 * O cache e limpo em `onSettled`, e nao em `onSuccess`: o `DELETE` responde `204` sempre, mas
 * se a **rede** cair no meio o usuario clicou em sair e precisa sair. Guardar a sessao local
 * porque o servidor nao respondeu deixaria a tela mostrando alguem que ja pediu para sair.
 * O cookie continua no navegador nesse caso — e por isso quem limpa de verdade e o servidor —,
 * mas a tela nao mente.
 *
 * `aoTerminar` e onde o chamador navega para `/entrar`: a navegacao e do componente, que tem
 * o roteador; a limpeza do cache e daqui.
 */
export function useSair(aoTerminar?: () => void) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: () => sair(),
    onSettled: () => {
      clienteDeConsultas.clear()
      aoTerminar?.()
    },
  })
}
